/**
 * Service centralisé pour l'écoute des événements Socket.IO
 *
 * Ce service est initialisé une seule fois au démarrage de l'application
 * et gère tous les événements WebSocket de manière globale.
 */

import { initSocket, onEvent, offEvent, disconnectSocket } from '@/config/socket'
import { useNotificationStore } from '@/stores/notificationStore'

// Flag pour éviter les initialisations multiples
let isInitialized = false

// Références aux handlers pour pouvoir les désinscrire
let newMessageHandler: ((data: any) => void) | null = null
let messagesReadHandler: ((data: any) => void) | null = null

/**
 * Jouer un son de notification
 */
const playNotificationSound = () => {
  try {
    const audio = new Audio('/sounds/notification.mp3')
    audio.volume = 0.3
    audio.play().catch(() => {
      // Ignorer l'erreur si l'autoplay est bloqué
    })
  } catch (e) {
    // Ignorer si le son n'existe pas
  }
}

/**
 * Initialiser les listeners Socket.IO globaux
 * Cette fonction doit être appelée UNE SEULE FOIS au démarrage de l'app
 * après que l'utilisateur soit authentifié
 */
export const initSocketListeners = (): void => {
  // Éviter les initialisations multiples
  if (isInitialized) {
    console.log('[SocketListenerService] Déjà initialisé, skip')
    return
  }

  // Vérifier si l'utilisateur est authentifié
  const token = localStorage.getItem('LS_COOP_FINANCE_BO_ACCESS_TOKEN')
  if (!token) {
    console.log('[SocketListenerService] Pas de token, skip initialisation')
    return
  }

  console.log('[SocketListenerService] 🚀 Initialisation des listeners globaux...')

  // Initialiser la connexion Socket.IO
  const socket = initSocket()

  if (!socket) {
    console.warn('[SocketListenerService] Socket non disponible')
    return
  }

  // Récupérer le store de notifications
  const notificationStore = useNotificationStore()

  // Handler pour les nouveaux messages admin
  newMessageHandler = (data: any) => {
    console.log('[SocketListenerService] 📨 Nouveau message admin reçu:', data)

    // Extraire conversation_id de différentes structures possibles
    const conversationId = data?.conversation_id ||
                          data?.message?.conversation_id ||
                          data?.conversationId ||
                          data?.message?.conversationId

    if (conversationId) {
      console.log('[SocketListenerService] Incrémentation du compteur pour conversation:', conversationId)
      notificationStore.incrementUnread(conversationId)

      // Jouer un son de notification
      playNotificationSound()
    } else {
      console.warn('[SocketListenerService] ⚠️ Aucun conversation_id trouvé:', data)
    }
  }

  // Handler pour les messages lus
  messagesReadHandler = (data: any) => {
    console.log('[SocketListenerService] 📖 Messages lus:', data)

    const conversationId = data?.conversation_id || data?.conversationId

    if (conversationId) {
      notificationStore.handleMessagesRead(conversationId)
    } else {
      console.warn('[SocketListenerService] ⚠️ Aucun conversation_id trouvé dans les données de lecture:', data)
    }
  }

  // Enregistrer les listeners
  onEvent('new.message.admin', newMessageHandler)
  onEvent('messages.read', messagesReadHandler)

  isInitialized = true
  console.log('[SocketListenerService] ✅ Listeners globaux enregistrés avec succès')
}

/**
 * Nettoyer les listeners Socket.IO
 * À appeler lors de la déconnexion de l'utilisateur
 */
export const cleanupSocketListeners = (): void => {
  console.log('[SocketListenerService] 🧹 Nettoyage des listeners...')

  if (newMessageHandler) {
    offEvent('new.message.admin', newMessageHandler)
    newMessageHandler = null
  }

  if (messagesReadHandler) {
    offEvent('messages.read', messagesReadHandler)
    messagesReadHandler = null
  }

  disconnectSocket()
  isInitialized = false

  console.log('[SocketListenerService] ✅ Listeners nettoyés')
}

/**
 * Réinitialiser les listeners (utile après une reconnexion)
 */
export const reinitSocketListeners = (): void => {
  console.log('[SocketListenerService] 🔄 Réinitialisation des listeners...')
  cleanupSocketListeners()
  initSocketListeners()
}

/**
 * Vérifier si les listeners sont initialisés
 */
export const isSocketListenersInitialized = (): boolean => {
  return isInitialized
}
